import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Brain, ShuffleAngular, Eye, ChartLineUp, CalendarCheck, ArrowRight } from "@phosphor-icons/react";
import { E } from "@/components/Editable";

const blocks = [
  {
    icon: Brain, id: "ARC-001", title: "Jungian Archetypes",
    text: "Carl Jung saw the 22 Major Arcana as a map of the collective unconscious — The Hero, The Shadow, The Anima. When a card resonates, it's your psyche recognizing a pattern it already knows.",
  },
  {
    icon: ShuffleAngular, id: "PRB-002", title: "Structured Randomness",
    text: "A 3-card draw from 78 cards with orientation yields 3,651,648 unique spreads. Randomness strips away your rehearsed narrative and forces a fresh angle on the same question.",
  },
  {
    icon: Eye, id: "PRJ-003", title: "The Projective Mirror",
    text: "Like the Rorschach inkblot, tarot imagery is deliberately ambiguous. What you see in The Tower says less about the card and more about what your mind is ready to process.",
  },
  {
    icon: ChartLineUp, id: "PTN-004", title: "Pattern Interruption",
    text: "Neuroscience calls it 'cognitive defusion' — stepping outside your loop of automatic thoughts. A spread externalizes the problem so your prefrontal cortex can finally examine it.",
  },
];

const stats = [
  ["78", "cards in a full deck"],
  ["3,651,648", "possible 3-card spreads"],
  ["22", "Jungian archetypes in the Major Arcana"],
  ["1440", "first recorded deck, Milan, Italy"],
];

export const ScienceSection = () => (
  <section className="relative py-24 lg:py-36 overflow-hidden" id="science" data-testid="science-section">
    <div
      className="absolute inset-0 opacity-20 bg-cover bg-center"
      style={{ backgroundImage: "url(https://images.unsplash.com/photo-1625517057325-ff548e5f0049?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwxfHxzYWNyZWQlMjBnZW9tZXRyeSUyMGRhcmt8ZW58MHx8fHwxNzg0MDE2Mjk5fDA&ixlib=rb-4.1.0&q=85)" }}
    />
    <div className="absolute inset-0 bg-gradient-to-b from-background via-transparent to-background" />
    <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-12 mb-16">
        <div className="md:col-span-7">
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-gold mb-4"><E k="science_overline" f="// The technical science" /></p>
          <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tighter leading-tight">
            <E k="science_heading_line1" f="Tarot isn't magic." /><br />
            <span className="gold-text italic"><E k="science_heading_line2" f="It's applied psychology." /></span>
          </h2>
        </div>
        <div className="md:col-span-4 md:col-start-9 flex items-end">
          <p className="text-sm text-muted-foreground leading-relaxed">
            <E k="science_intro" f="Behind the velvet and candlelight sits a 600-year-old cognitive technology: archetypes, probability and projection working together to show you what you already know." />
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 border border-white/10 divide-y sm:divide-y-0 divide-white/10 sm:[&>*:not(:first-child)]:border-l sm:[&>*:nth-child(3)]:border-t lg:[&>*]:!border-t-0 sm:[&>*:nth-child(4)]:border-t sm:[&>*]:border-white/10">
        {blocks.map((b, i) => (
          <motion.div
            key={b.id}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.5, delay: i * 0.12 }}
            className="p-8 bg-black/40 backdrop-blur-sm hover:bg-black/60 transition-colors duration-300"
            data-testid={`science-block-${i}`}
          >
            <div className="flex items-center justify-between mb-6">
              <b.icon size={30} weight="duotone" className="text-gold" />
              <span className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{b.id}</span>
            </div>
            <h3 className="font-heading text-2xl font-semibold tracking-tight mb-3"><E k={`science_block_${i}_title`} f={b.title} /></h3>
            <p className="text-xs text-muted-foreground leading-relaxed"><E k={`science_block_${i}_text`} f={b.text} /></p>
          </motion.div>
        ))}
      </div>

      <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10 border border-white/10" data-testid="science-stats-strip">
        {stats.map(([num, label], i) => (
          <div key={label} className="bg-background p-6 text-center">
            <p className="font-heading text-3xl sm:text-4xl font-bold text-gold"><E k={`science_stat_${i}_num`} f={num} /></p>
            <p className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground mt-2"><E k={`science_stat_${i}_label`} f={label} /></p>
          </div>
        ))}
      </div>

      <div className="mt-14 text-center">
        <Link
          to="/book"
          data-testid="science-book-cta"
          className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-gold text-black text-xs uppercase tracking-[0.2em] font-bold hover:bg-[#e8c95a] transition-colors duration-300"
        >
          <CalendarCheck size={16} weight="fill" /> <E k="science_cta" f="Talk to a Reader — Book Now" /> <ArrowRight size={16} />
        </Link>
      </div>
    </div>
  </section>
);
